import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../context/auth';
import { useAsync } from '../lib/hooks';
import { useLang } from '../lib/useLang';
import { formatBytes, formatDateTime, formatNumber } from '../lib/format';
import { PageHeader } from '../components/PageHeader';
import { ApiState, EmptyState } from '../components/States';
import { StorageDonut } from '../components/StorageDonut';
import { Badge, Card, CardHead, Stat } from '../components/ui';
import { RetentionBadge } from './shared';
import {
  IconArchive,
  IconBilling,
  IconPlus,
  IconChevronRight,
} from '../components/Icons';

export function Dashboard() {
  // Purchased volume vs. what has actually been burned to glass.
  const quota = useAsync(() => api.quota(), []);
  const { t } = useTranslation();
  const lang = useLang();
  const { me } = useAuth();
  const archives = useAsync(() => api.listArchives(100), []);
  const audit = useAsync(() => api.listAudit(6), []);

  return (
    <>
      <PageHeader
        title={t('dashboard.welcome', { name: me.username })}
        subtitle={t('dashboard.subtitle')}
        actions={
          <Link className="btn btn--primary" to="/archives">
            <IconPlus width={18} height={18} />
            {t('dashboard.quickCreate')}
          </Link>
        }
      />

      <ApiState {...archives} onRetry={archives.reload}>
        {(page) => {
          const totalObjects = page.items.reduce((s, a) => s + a.objects, 0);
          const totalBytes = page.items.reduce((s, a) => s + a.bytes, 0);
          return (
            <div className="stack">
              <div className="grid grid--stats">
                <Stat
                  label={t('dashboard.totalArchives')}
                  value={formatNumber(page.items.length, lang)}
                  icon={<IconArchive width={16} height={16} />}
                />
                <Stat label={t('dashboard.totalObjects')} value={formatNumber(totalObjects, lang)} icon={<IconArchive width={16} height={16} />} />
                <Stat
                  label={t('dashboard.totalBytes')}
                  value={formatBytes(totalBytes, lang)}
                  icon={<IconBilling width={16} height={16} />}
                />
              </div>

              {quota.data ? (
                <Card>
                  <CardHead title={t('quota.title')}>
                    {quota.data.planName ? (
                      <span className="muted" style={{ fontSize: 13 }}>{quota.data.planName}</span>
                    ) : null}
                  </CardHead>
                  <div className="card__body">
                    {quota.data.hasQuota ? (
                      <>
                        <div className="grid grid--stats">
                          <Stat label={t('quota.purchased')} value={formatBytes(quota.data.purchasedBytes, lang)} />
                          <Stat label={t('quota.used')} value={formatBytes(quota.data.burnedBytes, lang)} />
                          <Stat label={t('quota.remaining')} value={formatBytes(quota.data.remainingBytes, lang)} />
                        </div>
                        <div style={{ marginTop: 16 }}>
                          <StorageDonut quota={quota.data} lang={lang} />
                        </div>
                        {/* The remaining figure above is the plan's own (purchased
                            minus burned) and deliberately ignores staged data;
                            the chart splits that remainder further, so the
                            already-committed share is spelled out here. */}
                        <p className="muted" style={{ fontSize: 13, marginTop: 12, marginBottom: 0 }}>
                          {quota.data.burnedBytes > quota.data.purchasedBytes
                            ? t('quota.chart.overQuota', {
                                over: formatBytes(quota.data.burnedBytes - quota.data.purchasedBytes, lang),
                              })
                            : t('quota.chart.committedHint', {
                                committed: formatBytes(quota.data.burnedBytes + quota.data.stagedBytes, lang),
                                staged: formatBytes(quota.data.stagedBytes, lang),
                              })}
                        </p>
                        <p className="muted" style={{ fontSize: 13, marginTop: 8, marginBottom: 0 }}>
                          {t('quota.hint', {
                            percent: Math.round(quota.data.percent * 100),
                            snapshots: quota.data.snapshots,
                            staged: formatBytes(quota.data.stagedBytes, lang),
                          })}
                        </p>
                      </>
                    ) : (
                      <>
                        <div className="grid grid--stats">
                          <Stat label={t('quota.used')} value={formatBytes(quota.data.burnedBytes, lang)} />
                          <Stat label={t('quota.staged')} value={formatBytes(quota.data.stagedBytes, lang)} />
                        </div>
                        <div style={{ marginTop: 16 }}>
                          <StorageDonut quota={quota.data} lang={lang} />
                        </div>
                        <p className="muted" style={{ fontSize: 13, marginTop: 12, marginBottom: 0 }}>
                          {t('quota.noQuota')}
                        </p>
                      </>
                    )}
                  </div>
                </Card>
              ) : null}

              <Card>
                <CardHead title={t('dashboard.yourArchives')}>
                  <Link className="btn btn--ghost btn--sm" to="/archives">
                    {t('dashboard.viewAll')} <IconChevronRight width={16} height={16} />
                  </Link>
                </CardHead>
                {page.items.length === 0 ? (
                  <EmptyState title={t('archives.empty')} />
                ) : (
                  <div className="table-wrap">
                    <table className="data">
                      <thead>
                        <tr>
                          <th>{t('common.name')}</th>
                          <th className="num">{t('archives.colObjects')}</th>
                          <th className="num">{t('archives.colSize')}</th>
                          <th>{t('archives.colRetention')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {page.items.slice(0, 6).map((a) => (
                          <tr key={a.id}>
                            <td className="cell-strong">
                              <Link to={`/archives/${a.id}`}>{a.name}</Link>
                            </td>
                            <td className="num">{formatNumber(a.objects, lang)}</td>
                            <td className="num">{formatBytes(a.bytes, lang)}</td>
                            <td>
                              <RetentionBadge policy={a.retentionPolicy} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>

              <Card>
                <CardHead title={t('dashboard.recentActivity')} />
                <ApiState {...audit} onRetry={audit.reload}>
                  {(ev) =>
                    ev.items.length === 0 ? (
                      <EmptyState title={t('dashboard.noActivity')} />
                    ) : (
                      <div className="table-wrap">
                        <table className="data">
                          <tbody>
                            {ev.items.map((e) => (
                              <tr key={e.seq}>
                                <td className="muted mono" style={{ whiteSpace: 'nowrap' }}>
                                  {formatDateTime(e.occurredAt, lang)}
                                </td>
                                <td className="cell-strong">{e.action}</td>
                                <td className="muted">{e.actor}</td>
                                <td>
                                  <Badge tone={e.result === 'OK' ? 'success' : 'default'}>{e.result}</Badge>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )
                  }
                </ApiState>
              </Card>
            </div>
          );
        }}
      </ApiState>
    </>
  );
}
